<template>
  <v-container fluid class="align-start align-content-start fill-height">
    <div class="bs-table-header pt-8">
      <v-row justify="space-between" class="align-end mb-3 px-4">
        <v-col cols="4">
          <h1 class="bs-header-title">
            {{ $t('components.favourites.title') }}
          </h1>
        </v-col>
        <v-spacer />
        <v-col class="d-flex align-center justify-end">
          <v-btn class="btn btn-default" @click="$emit('addFavourite')" height="36px" depressed>
            <v-icon left>mdi-plus</v-icon>
            {{ $t('components.favourites.add') }}
          </v-btn>
        </v-col>
      </v-row>
    </div>
    <div class="bs-table-table">
      <v-row v-if="results.length > 0" no-gutters>
        <v-col class="col-withou-top-padding table-color">
          <v-data-table
            class="bs-table elevation-0"
            :headers="headers"
            :items="results"
            :server-items-length="totalFavourites"
            calculate-widths
            :multi-sort="false"
            :must-sort="true"
            :single-expand="true"
            :page.sync="page"
            :options.sync="options"
            :items-per-page="itemsPerPage"
            hide-default-footer
            @page-count="pageCount = $event"
          >
            <template v-slot:item.alias="{ item }">
              <div class="d-flex align-center">
                <favourites-icon style="color: #47d061; width:24px" />
                <span class="ml-2">{{ item.alias }}</span>
              </div>
            </template>
            <template v-slot:item.suggestForPickupAddress="{ item }">
              <v-checkbox
                v-model="item.suggestForPickupAddress"
                color="#47D061"
                on-icon="mdi-check-circle-outline"
                off-icon="mdi-checkbox-blank-circle-outline"
                @click="handleFavouriteAddressSuggestion(item)"
              />
            </template>

            <template v-slot:item.suggestForDestinationAddress="{ item }">
              <v-checkbox
                v-model="item.suggestForDestinationAddress"
                color="#47D061"
                on-icon="mdi-check-circle-outline"
                off-icon="mdi-checkbox-blank-circle-outline"
                @click="handleFavouriteAddressSuggestion(item)"
              />
            </template>

            <template v-slot:item.actions="{ item }">
              <delete-icon
                @click="removeFavouriteAddress(item.id)"
                class="icon-control-list"
              />
            </template>
          </v-data-table>
          <div class="d-flex justify-between align-center py-2 px-4 bs-table-footer">
            <v-row no-gutters class="bs-per-page">
              <span>
                {{ $t(`components.tripsHistory.perPage`) }}
              </span>
              <span
                v-for="item  in [10, 20, 30]"
                :key="item"
                class="px-1"
                :class="{ 'active-page-count': item === options.itemsPerPage }"
                @click="options.itemsPerPage = item"
              >
                {{ item }}
              </span>
            </v-row>
            <v-pagination
              v-model="page"
              :length="pageCount"
              :total-visible="7"
              color="#f2f2f2"
              class="bs-pagination"
            ></v-pagination>
          </div>
        </v-col>
      </v-row>
      <v-row v-else no-gutters class="empty-state">
        <v-col cols="12" class="d-flex flex-column justify-center align-center">
          <favourites-icon style="color: #47d061; width: 50px;" />
          <span class="empty-state-text mt-5">
            {{ $t('components.favourites.table.empty-state') }}
          </span>
          <span class="empty-state-description">
            {{ $t('components.favourites.table.empty-state-description') }}
          </span>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import { FAVOURITE_HEADER } from '@/constants/tables';
import { iconLoader } from '@/helpers/iconsLoader';
import { mapGetters } from 'vuex';

export default {
  name: 'favourite-table',
  props: {
    results: {
      type: Array,
      default: () => [],
    },
    totalFavourites: {
      type: Number,
      default: 0,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ...iconLoader.favourites,
    ...iconLoader.elements,
  },
  data: () => ({
    headers: FAVOURITE_HEADER,
    page: 1,
    pageCount: 0,
    itemsPerPage: 10,
    options: {},
  }),
  watch: {
    getSelectedCorporationId: {
      handler() {
        this.loadData();
      },
      immediate: true,
    },
    options: {
      handler() {
        this.loadData();
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters('corporations', ['getSelectedCorporationId']),
  },
  methods: {
    async loadData() {
      if (!this.getSelectedCorporationId) return;

      const {
        page, itemsPerPage,
      } = this.options;
      this.$emit('callLoadData', {
        payload: {
          page: page - 1 || 0,
          maxPerPage: itemsPerPage,
        },
        businessAccountId: this.getSelectedCorporationId,
      });
    },
    async removeFavouriteAddress(id) {
      const response = await this.$store.dispatch('favourites/deleteFavouriteAddress', { id });

      if (response) {
        this.results = this.results.filter((el) => el.id !== id);
      }
    },
    handleFavouriteAddressSuggestion(item) {
      this.$store.dispatch('favourites/updateFavouriteAddress', {
        id: item.id,
        payload: {
          suggestForPickupAddress: item.suggestForPickupAddress,
          suggestForDestinationAddress: item.suggestForDestinationAddress,
        },
      });
    },
  },
};
</script>

<style lang="scss">
.empty-state {
  height: 70vh;
  background-color: #f8f8f8;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.empty-state-text {
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 25px;

  /* identical to box height, or 139% */
  display: flex;
  align-items: center;
  text-align: center;
  letter-spacing: -0.005em;

  /* Day/Grey0 */
  color: #333333;
}

.empty-state-description {
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;

  /* or 150% */
  display: flex;
  align-items: center;
  text-align: center;
  letter-spacing: -0.005em;

  /* Day/Grey1 */
  color: #888888;
}
</style>
